import type {
  CoachingGoal,
  CoachingGoalMilestone,
  CoachingPlan,
} from '@efdigital/ll-addon-data';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import GoalCard from '@efdigital/ll-xceed-v2/components/coaching/goal-card.gts';
import GoalCreateForm from '@efdigital/ll-xceed-v2/components/coaching/goal-create-form.gts';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface GoalsListSignature {
  Args: {
    goals: readonly CoachingGoal[];
    milestones: readonly CoachingGoalMilestone[];
    /** Plan new goals hang off. Required to create; omitted disables the form. */
    plan: CoachingPlan | null;
    /**
     * Owning coachee's user id. Posted as `user` on create so a coach-set
     * goal is owned by the coachee, not the coach.
     */
    ownerUserId: string;
    role: 'coach' | 'coachee';
    goalRoute?: string;
    goalRouteModels?: unknown[];
    /** When false, hides the create form until toggled on. Defaults to true. */
    showCreateForm?: boolean;
    onGoalCreated?: () => void;
    onCloseCreateForm?: () => void;
  };
  Element: HTMLDivElement;
}

function eq(left: unknown, right: unknown): boolean {
  return left === right;
}

/**
 * Shared goals list for the coach's coachee page and the coachee's own list.
 *
 * Creating posts `user` as the coachee. Editing uses a sparse PATCH so a coach
 * can change wording or status without sending the pinned ownership fields.
 */
export default class GoalsList extends Component<GoalsListSignature> {
  @tracked editingId: string | null = null;

  get canCreate(): boolean {
    return Boolean(this.args.plan);
  }

  get shouldShowCreateForm(): boolean {
    return this.canCreate && (this.args.showCreateForm ?? true);
  }

  isEditing = (goal: CoachingGoal): boolean => this.editingId === goal.id;

  startEditing = (goal: CoachingGoal) => {
    this.editingId = goal.id;
  };

  cancelEditing = () => {
    this.editingId = null;
  };

  <template>
    <div ...attributes>
      {{#if this.canCreate}}
        {{#if this.shouldShowCreateForm}}
          <GoalCreateForm
            @plan={{@plan}}
            @ownerUserId={{@ownerUserId}}
            @milestones={{@milestones}}
            @onCreated={{@onGoalCreated}}
            @onClose={{@onCloseCreateForm}}
          />
        {{/if}}
      {{/if}}

      {{#if @goals.length}}
        <ul
          class="list-unstyled d-flex flex-column gap-3 mb-0 align-items-stretch"
        >
          {{#each @goals as |goal|}}
            {{#if (this.isEditing goal)}}
              <li>
                <GoalCreateForm
                  @goal={{goal}}
                  @milestones={{@milestones}}
                  @onCreated={{this.cancelEditing}}
                  @onClose={{this.cancelEditing}}
                  @onDeleted={{this.cancelEditing}}
                />
              </li>
            {{else}}
              <GoalCard
                @goal={{goal}}
                @milestones={{@milestones}}
                @route={{@goalRoute}}
                @routeModels={{@goalRouteModels}}
                @onOpen={{this.startEditing}}
              />
            {{/if}}
          {{/each}}
        </ul>
      {{else}}
        <EmptyState
          @message={{if
            (eq @role "coach")
            "No goals yet. Add one your coachee should work towards."
            "No goals yet. Add one, or your coach can add one for you."
          }}
          @icon="ph:flag"
        />
      {{/if}}
    </div>
  </template>
}
